// Finanzen (Screens eigentuemer/03 und 07) – nur Soll-Werte aus gespeicherten Daten: heutige Mieten je Einheit
// (wo wir vermieten) und das monatliche Hausgeld (WEG). Zahlungseingänge, Eigentümerabrechnung, Auszahlungen,
// Rücklagenstand und Jahresabrechnung fehlen, bis es die Buchhaltung gibt (Entscheidung 22.10.2026: ausblenden).
import { Bank, ChartBar, Wallet } from '@phosphor-icons/react';
import { GlasWidget, LeerZustand } from '../../components/portal/bausteine';
import { SteuerpaketWidget } from '../../components/portal/SteuerpaketWidget';
import { artIstWeg, zahlDe, type EigDaten, type ObjektSicht } from '../../lib/portal/eigentuemer';
import { euro, hausgeldTeile, sollJeEinheit } from '../../lib/portal/eigentuemerE7';

const ZEILE = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, background: 'var(--p-w05)', minWidth: 0 } as const;

export default function EigentuemerFinanzen({ daten, sicht, heute }: { daten: EigDaten; sicht: ObjektSicht; heute: string }) {
  const mieten = sollJeEinheit(daten, sicht, heute);
  const summeKalt = mieten.reduce((s, m) => s + m.kalt, 0);
  const summeGesamt = mieten.reduce((s, m) => s + m.gesamt, 0);
  const hausgeld = artIstWeg(sicht.art) ? hausgeldTeile(daten, sicht) : [];
  const hausgeldSumme = hausgeld.reduce((s, t) => s + t.betrag, 0);

  if (!mieten.length && !hausgeld.length) {
    return (
      <div className="p-rein mp-karten">
        <SteuerpaketWidget />
        <LeerZustand icon={ChartBar} text="Zu diesem Objekt liegen noch keine Finanzzahlen vor." />
      </div>
    );
  }

  return (
    <div className="p-rein mp-karten">
      <SteuerpaketWidget />
      {mieten.length > 0 && (
        <GlasWidget farbe="konto" icon={Wallet} titel="Mieteinnahmen (Soll)"
          rechts={<span style={{ fontSize: 12, fontWeight: 800, color: 'var(--p-w50)' }}>je Monat</span>}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
            <div style={{ ...ZEILE, flexDirection: 'column', alignItems: 'flex-start', gap: 3 }} data-tip="Summe der Kaltmieten der laufenden Mietverhältnisse">
              <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--p-w45)' }}>Kaltmiete</span>
              <span className="p-zahl" style={{ fontSize: 20, fontWeight: 800 }}>{euro(summeKalt)}</span>
            </div>
            <div style={{ ...ZEILE, flexDirection: 'column', alignItems: 'flex-start', gap: 3 }} data-tip="Kaltmiete plus Vorauszahlungen für Neben- und Heizkosten">
              <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--p-w45)' }}>Gesamtmiete</span>
              <span className="p-zahl" style={{ fontSize: 20, fontWeight: 800 }}>{euro(summeGesamt)}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {mieten.map((m) => (
              <div key={m.bezeichnung} style={ZEILE}>
                <span className="p-kuerzen" style={{ minWidth: 52, maxWidth: '35%', fontSize: 13, fontWeight: 800 }}>{m.bezeichnung}</span>
                <span className="p-kuerzen" style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--p-w55)' }}>{m.mieter ?? 'Leerstand'}</span>
                <span className="p-zahl" style={{ fontSize: 13.5, fontWeight: 800, whiteSpace: 'nowrap' }}>{m.gesamt > 0 ? euro(m.gesamt) : '–'}</span>
              </div>
            ))}
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w45)' }}>Vereinbarte Mieten laut Mietvertrag, Stand heute.</span>
        </GlasWidget>
      )}

      {hausgeld.length > 0 && (
        <GlasWidget farbe="schluessel" icon={Bank} titel="Hausgeld"
          rechts={<span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--p-blau-link)' }}>{euro(hausgeldSumme)} / Monat</span>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {hausgeld.map((t) => (
              <div key={t.label} style={ZEILE}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{t.label}</span>
                <span className="p-zahl" style={{ fontSize: 13.5, fontWeight: 800 }}>{euro(t.betrag)}</span>
              </div>
            ))}
          </div>
          {sicht.mea > 0 && (
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w45)' }}>
              Ihr Anteil {zahlDe(sicht.mea, 2)}{sicht.meaGesamt > 0 ? ` / ${zahlDe(sicht.meaGesamt, 2)}` : ''} MEA · {sicht.einheiten.map((e) => e.bezeichnung).join(', ')}
            </span>
          )}
        </GlasWidget>
      )}
    </div>
  );
}
